/**
 * StorefrontToggleCell — inline toggle for storefront visibility in the Item List.
 *
 * Calls the dedicated toggle_storefront_visibility API which updates the
 * Item Storefront Visibility child table (source of truth for storefronts)
 * and syncs the legacy storefront_enabled field for backward compatibility.
 */

import { useState, useEffect } from "react";
import { Switch } from "@/components/ui/switch";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { getERPClient } from "@/lib/erp/client";
import { toast } from "sonner";

type StorefrontToggleCellProps = {
  itemName: string;
  storefrontEnabled: boolean;
};

export function StorefrontToggleCell({ itemName, storefrontEnabled }: StorefrontToggleCellProps) {
  const [localEnabled, setLocalEnabled] = useState(storefrontEnabled);
  const queryClient = useQueryClient();

  // Sync with prop changes (e.g., after React Query refetch)
  useEffect(() => {
    setLocalEnabled(storefrontEnabled);
  }, [storefrontEnabled]);

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async (enabled: boolean) => {
      const client = getERPClient();
      return client.call(
        "zaviago_backend.storefront.api_visibility_toggle.toggle_storefront_visibility",
        { item_code: itemName, enabled: enabled ? 1 : 0 },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["list", "Item"] });
    },
  });

  const handleToggle = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();

    const prev = localEnabled;
    const next = !localEnabled;
    setLocalEnabled(next);

    try {
      await mutateAsync(next);
    } catch {
      setLocalEnabled(prev);
      toast.error("Failed to update storefront visibility. Please try again.");
    }
  };

  return (
    <div
      className="flex items-center justify-center min-h-[44px] min-w-[44px]"
      onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
    >
      <Switch
        checked={localEnabled}
        onCheckedChange={() => {}} // Noop: toggle handled by onClick which provides event for stopPropagation
        onClick={handleToggle}
        disabled={isPending}
        aria-label="Publish to storefront"
        className="scale-[0.65] origin-center"
      />
    </div>
  );
}
